import { App } from 'vue';
import {
	IAppRegistration,
	IRouting,
	SearchPageAppRegistrationRequestedEvent,
	SearchPageSwitchTabsAvailableEvent,
	SearchPageSwitchRequestedEvent,
	SearchType,
	IsAppRegistration,
	IAppState,
	SearchPageFilterStateChangedEvent,
	SearchPageCacheReadyEvent,
} from '@studyportals/search-page-interface';
import { Routing } from './routing/Routing';
import { IEventAggregationService, ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { PortalType } from '@studyportals/domain-client';
import { SwitchTab } from './SwitchTab';
import { EventAggregationService } from './platform/EventAggregationService';
import { ActivatedApp } from './ActivatedApp';
import { FilterPersistence } from './persistence/FilterPersistence';
import { DataStorage } from '@studyportals/data-storage';
import { IDataStorage } from './persistence/IDataStorage';
import { Cache } from './caching/Cache';
import { SearchPageRefreshText } from '@/search-page-interface';


declare const INITIALIZE_FILTERS_FROM_PERSISTENCE: boolean;

export class SearchPage implements
	ISubscriber<SearchPageAppRegistrationRequestedEvent>,
	ISubscriber<SearchPageSwitchRequestedEvent>,
	ISubscriber<SearchPageFilterStateChangedEvent> {
	public static readonly instance = new SearchPage();

	private readonly cache: Cache;

	private readonly eventAggregationService: IEventAggregationService;
	private readonly filterPersistence: FilterPersistence;
	private readonly activatedApp: ActivatedApp;
	private readonly _routing: Routing;

	private readonly applications: IAppRegistration[] = [];
	private readonly portalType: PortalType;

	public constructor(
		eventAggregationService?: IEventAggregationService,
		portalType?: PortalType,
		routing?: Routing,
		dataStorage?: IDataStorage,
	) {
		this.cache = new Cache();

		this.filterPersistence = new FilterPersistence(dataStorage ?? DataStorage);
		this.activatedApp = new ActivatedApp(eventAggregationService);
		this._routing = routing ?? new Routing(eventAggregationService);

		this.portalType = portalType ?? window.PortalType ?? PortalType.PHD;
		this.eventAggregationService = eventAggregationService ?? EventAggregationService.instance;
		this.eventAggregationService.subscribeTo(SearchPageAppRegistrationRequestedEvent.EventType, this, true);
		this.eventAggregationService.subscribeTo(SearchPageSwitchRequestedEvent.EventType, this);
		this.eventAggregationService.subscribeTo(SearchPageFilterStateChangedEvent.EventType, this);

		this.eventAggregationService.publishTo(SearchPageCacheReadyEvent.EventType, new SearchPageCacheReadyEvent(this.cache));
	}

	public get routing(): IRouting {
		return this._routing;
	}

	public notify(event: SearchPageAppRegistrationRequestedEvent | SearchPageSwitchRequestedEvent | SearchPageFilterStateChangedEvent): void {
		if (SearchPageAppRegistrationRequestedEvent.is(event)) {
			this.registerApplication(event.appRegistration);
		}

		if (SearchPageSwitchRequestedEvent.is(event)) {
			if(event.searchType !== SearchType.STUDY) {
				EventAggregationService.instance.publishTo(SearchPageRefreshText.EventType, new SearchPageRefreshText(''));
			}
			this.activateApplication(event.searchType);
		}

		if (SearchPageFilterStateChangedEvent.is(event)) {
			this.updateFilterPersistence();
		}
	}

	public registerApplication(application: IAppRegistration): void {
		const isNotAppRegistration = !IsAppRegistration(application);
		if (isNotAppRegistration) {
			return;
		}

		this.initializeApplication(application);
		this._routing.registerApplication(application);

		this.notifyForAvailableSwitchTabs();
	}

	public activateApplication(searchType: SearchType): void {
		const application = this.findApplicationFor(searchType);
		if (application) {
			const activatedApp = this.activatedApp.application;
			const state = this.determineAppStateFrom(activatedApp);

			application.activate(state);
		}
	}

	private initializeApplication(application: IAppRegistration): void {
		this.applications.push(application);

		const state = INITIALIZE_FILTERS_FROM_PERSISTENCE ? this.readStateFromPersistence() : { filterSelection: {} };
		application.initializeFrom(state);
	}

	private updateFilterPersistence(): void {
		const activatedApp = this.activatedApp.application;
		const state = this.determineAppStateFrom(activatedApp);

		this.filterPersistence.updateFrom(state.filterSelection);
	}

	private findApplicationFor(searchType: SearchType): IAppRegistration | undefined {
		return this.applications.find((application) => application.type === searchType);
	}

	private isRegistered(searchType: SearchType): boolean {
		return this.findApplicationFor(searchType) !== undefined;
	}

	private determineAppStateFrom(activatedApp: IAppRegistration | null): IAppState {
		if (activatedApp === null) {
			return { filterSelection: {} };
		}

		const state = activatedApp.sleep();
		const availableFilters = activatedApp.availableFilters || [];
		for (const clearedFilter of availableFilters.filter((f) => !state.filterSelection[f])) {
			state.filterSelection[clearedFilter] = [];
		}

		return state;
	}

	private determineExpectedSearchTypes(): readonly SearchType[] {
		const searchTypes = [SearchType.STUDY];
		searchTypes.push(SearchType.ORGANISATION);

		const portalTypesWithScholarships = [PortalType.PHD, PortalType.BACHELOR, PortalType.MASTER];
		if (portalTypesWithScholarships.includes(this.portalType)) {
			searchTypes.push(SearchType.SCHOLARSHIP);
		}

		return searchTypes;
	}

	private determineAvailableSearchTypes(): readonly SearchType[] {
		return this.determineExpectedSearchTypes().filter((t) => this.isRegistered(t));
	}

	private notifyForAvailableSwitchTabs(): void {
		const availableSearchTypes = this.determineAvailableSearchTypes();

		if (availableSearchTypes.length > 1) {
			const availableSwitchTabs = SwitchTab.forSearchTypes(availableSearchTypes);

			this.eventAggregationService.publishTo(
				SearchPageSwitchTabsAvailableEvent.EventType,
				new SearchPageSwitchTabsAvailableEvent(availableSwitchTabs),
			);
		}
	}

	private readStateFromPersistence(): IAppState {
		return {
			filterSelection: this.filterPersistence.retrieve(),
		};
	}

	public install(app: App): void {
		this._routing.install(app);
	}
}
